import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import Modal from 'react-responsive-modal'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { createAchievPoint, updateAchievPoint } from '../../actions/achievPoints'
import { Input, Select } from '../../components/ModalInputs'
import Button from '../../components/Button'
import Confirmation from '../../components/Confirmation/Confirmation'
import styles from './styles.module.scss'

const INITIAL_VALUES = {
  achpoints: '',
  rachid: '',
  rlevelid: ''
}

const validationSchema = Yup.object().shape({
  achpoints: Yup.number()
    .required('Обязательное поле')
    .min(0, 'Минимальное значение 0'),
  rachid: Yup.string()
    .required('Обязательное поле'),
  rlevelid: Yup.string()
    .required('Обязательное поле'),
})

const AchievementPointsModal = ({
  open,
  onClose,
  data,
  addMode,
  achievements,
  eventLevels
}) => {
  const dispatch = useDispatch()
  const [confirmation, setConfirmation] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const createItem = useCallback(async (values) => {
    setIsSubmitting(true)
    try {
      const success = await dispatch(createAchievPoint(values))
      if (success) onClose()
    } finally {
      setIsSubmitting(false)
    }
  }, [dispatch, onClose])

  const updateItem = useCallback(async ({ rrowid, ...values }) => {
    setIsSubmitting(true)
    try {
      const success = await dispatch(updateAchievPoint(rrowid, values))
      if (success) onClose()
    } finally {
      setIsSubmitting(false)
    }
  }, [dispatch, onClose])

  const {
    values,
    errors,
    touched,
    dirty,
    submitCount,
    handleSubmit,
    handleChange,
    handleBlur,
    setValues,
    resetForm
  } = useFormik({
    initialValues: INITIAL_VALUES,
    enableReinitialize: true,
    validationSchema,
    onSubmit: addMode ? createItem : updateItem
  })

  useEffect(() => {
    if (!open) {
      resetForm()
      return
    }
    setValues(addMode ? INITIAL_VALUES : data)
  }, [open, addMode, data, setValues, resetForm])

  const handleCloseModal = useCallback(() => {
    if (dirty) {
      setConfirmation(true)
    } else {
      onClose()
    }
  }, [dirty, onClose])

  const acceptConfirmation = useCallback(() => {
    onClose()
    setConfirmation(false)
  }, [onClose])

  const rejectConfirmation = useCallback(() => {
    setConfirmation(false)
  }, [])

  const getError = useCallback((fieldName) =>
    (touched[fieldName] || submitCount > 0) && errors[fieldName],
    [touched, errors, submitCount]
  )

  const achievementOptions = useMemo(() =>
    achievements?.map(({ id, name }) => (
      <option key={id} value={id}>{name}</option>
    )) || [],
    [achievements]
  )

  const levelOptions = useMemo(() =>
    eventLevels?.map(({ news_level_id, news_level }) => (
      <option key={news_level_id} value={news_level_id}>{news_level}</option>
    )) || [],
    [eventLevels]
  )

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      classNames={{ modal: styles.modal }}
      closeOnEsc={!dirty}
      closeOnOverlayClick={!dirty}
      showCloseIcon={false}
      animationDuration={200}
      center
    >
      <form onSubmit={handleSubmit} className={styles.modalForm}>
        <div className={styles.modalMain}>
          <h2>{addMode ? 'Добавление баллов' : 'Редактирование баллов'}</h2>

          <Select
            label="Вид достижения"
            name="rachid"
            value={values.rachid || ''}
            error={getError('rachid')}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isSubmitting}
          >
            <option value="">Выберите достижение</option>
            {achievementOptions}
          </Select>

          <Select
            label="Уровень"
            name="rlevelid"
            value={values.rlevelid || ''}
            error={getError('rlevelid')}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isSubmitting}
          >
            <option value="">Выберите уровень</option>
            {levelOptions}
          </Select>

          <Input
            name="achpoints"
            label="Баллы"
            value={values.achpoints || ''}
            error={getError('achpoints')}
            onChange={handleChange}
            onBlur={handleBlur}
            type="number"
            min="0"
            disabled={isSubmitting}
          />
        </div>

        <div className={styles.modalFooter}>
          <div className={styles.footerInfo} />
          <div className={styles.footerButtons}>
            <Button
              mode="white"
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting || !dirty}
            >
              Сохранить
            </Button>
            <Button
              mode="white"
              type="button"
              onClick={handleCloseModal}
              disabled={isSubmitting}
            >
              Закрыть
            </Button>
          </div>
        </div>
      </form>

      {confirmation && (
        <Confirmation
          title="Есть несохраненные данные. Уверены что хотите выйти?"
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={rejectConfirmation}
        />
      )}
    </Modal>
  )
}

export default AchievementPointsModal
